/**
 * InternalSeichePage — Münnich/Wüest 1992 internal seiche & basin mixing
 *
 * V1H1 mode period: T = 2L · √(ρ / (g·Δρ·H_meta))
 * Basin-scale wind input + internal-wave dissipation budget.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Activity, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const G = 9.81;

export default function InternalSeichePage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('internal_seiche');
  const [L, setL] = useState(250);                  // basin length (m)
  const [H, setH] = useState(3.0);                  // total depth (m)
  const [H_epi, setH_epi] = useState(1.5);          // epilimnion thickness
  const [deltaT, setDeltaT] = useState(5);          // ΔT across thermocline
  const [U10, setU10] = useState(4.0);

  const R = useMemo(() => {
    // Density step across thermocline
    const dRho = 0.18 * deltaT;                       // kg/m³ per °C (Imberger 1985)
    const H_hyp = H - H_epi;
    // Reduced gravity
    const g_prime = G * dRho / 1000;
    // V1H1 mode period (Münnich 1992)
    const T_s = 2 * L * Math.sqrt(H_epi * H_hyp / (g_prime * H));
    // Frequency
    const f_s = 1 / T_s;
    // Wind stress
    const C_D = 0.0013, rho_air = 1.225;
    const tau_wind = C_D * rho_air * U10 * U10;
    // Brunt-Väisälä frequency
    const N = Math.sqrt(g_prime / Math.max(0.1, H));
    // Energy input rate (Wüest 1992 typical 1% of wind work)
    const u_star = Math.sqrt(tau_wind / 1000);        // m/s
    const eps_seiche = 0.01 * Math.pow(u_star, 3) / Math.max(1, H);
    // Wedderburn number
    const Wedderburn = (g_prime * H_epi * H_epi) / Math.max(1e-9, u_star * u_star * L);
    const upwellingRisk = Wedderburn < 1;
    return { T_s, f_s, tau_wind, N, u_star, eps_seiche, Wedderburn, upwellingRisk, dRho, g_prime };
  }, [L, H, H_epi, deltaT, U10]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';
  const fmtTime = (s: number) => s < 60 ? `${s.toFixed(0)} s` : s < 3600 ? `${(s / 60).toFixed(1)} min` : `${(s / 3600).toFixed(2)} h`;

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Activity} title="Module — Internal Seiche & Basin-Scale Mixing" subtitle="Münnich/Wüest V1H1 mode + Wedderburn upwelling risk"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('internal_seiche')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="internal_seiche" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-violet-300">V1H1 seiche period</div>
          <div className="text-2xl font-bold text-violet-200 mt-1">{fmtTime(R.T_s)}</div>
          <div className="text-xs text-slate-500 mt-1">f = {(R.f_s * 1000).toExponential(2)} mHz</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">N (Brunt-Väisälä)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{R.N.toExponential(2)}</div>
          <div className="text-xs text-slate-500 mt-1">rad/s</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">ε seiche</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{R.eps_seiche.toExponential(2)}</div>
          <div className="text-xs text-slate-500 mt-1">W/kg (1% of wind work)</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.upwellingRisk ? 'border-red-700 bg-red-900/15' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">Wedderburn W</div>
          <div className={`text-2xl font-bold mt-1 ${R.upwellingRisk ? 'text-red-200' : 'text-emerald-200'}`}>{fmt(R.Wedderburn, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">{R.upwellingRisk ? '< 1: full upwelling possible' : '≥ 1: stratification holds'}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
        {[
          { l: 'Basin length L (m)',           v: L,    set: setL,    min: 50, max: 2000, step: 10 },
          { l: 'Total depth H (m)',             v: H,    set: setH,    min: 1, max: 30, step: 0.5 },
          { l: 'Epilimnion H_e (m)',            v: H_epi, set: setH_epi, min: 0.3, max: H - 0.5, step: 0.1 },
          { l: 'ΔT thermocline (°C)',          v: deltaT, set: setDeltaT, min: 0, max: 15, step: 0.5 },
          { l: 'Wind U10 (m/s)',                v: U10,  set: setU10,  min: 0, max: 15, step: 0.5 },
        ].map(f => (
          <div key={f.l}>
            <label className="text-slate-400 block mb-1">{f.l}: <strong className="text-slate-100">{fmt(f.v, 1)}</strong></label>
            <input type="range" min={f.min} max={f.max} step={f.step} value={f.v}
              onChange={e => f.set(Number(e.target.value))} className="w-full accent-sky-500" />
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Methodology</div>
        <p>The V1H1 (vertical mode 1, horizontal mode 1) internal seiche is the fundamental basin-scale oscillation
        of the thermocline. Wind tilts the thermocline; when wind drops, gravity restores it, setting up a standing
        wave. The period T = 2L·√(H_e·H_h / (g'·H)) where g' = g·Δρ/ρ.</p>
        <p className="mt-2">The <strong className="text-violet-300">Wedderburn number W</strong> is the ratio of
        baroclinic-restoring force to wind-stress upwelling force. W &lt; 1 means wind can fully tilt the thermocline
        to the surface — upwelling at the lee shore brings hypolimnetic (potentially anoxic, high-P) water to the
        abstraction zone. Critical for source-protection planning.</p>
        <p className="mt-2">For Barcombe (L ≈ 250 m, H ≈ 3 m, modest stratification) W is typically &gt;1 and seiche
        periods are short (~5-15 min). Larger / deeper reservoirs can have hour-scale seiches with significant
        bottom mixing.</p>
        <p className="italic text-slate-500 mt-2">References: Wüest, Brooks & Imboden 1992; Münnich, Wüest & Imboden 1992; Imberger 1985 (Wedderburn).</p>
      </div>
    </div>
  );
}
